import { TableStorage } from './data-table.service';

/**
 * Changing a column's default visibility is not enough to change what people see. The presenter reads a saved
 * `hidden_columns` list in preference to the column defaults (`DataTablePresenter.getHiddenColumnIds`), and
 * anyone who has ever toggled a column has one — so a new `show: false` column appears visible to them, and a
 * column made visible by default stays hidden for anyone whose list already hides it.
 *
 * A migration adjusts a saved list exactly once and is recorded as applied even when there is no saved list
 * (then the defaults already say the right thing): a user who later toggles one of its columns on purpose must
 * not have that undone on the next load.
 *
 * - `hide` (the default): appends the columns to the saved list — for a new column that starts hidden.
 * - `show`: removes them from the saved list — for a column that is now visible by default.
 *
 * Moved here from the provider Claims page (FE-124) so the Support claims list can use it too.
 */
export interface IHiddenColumnsMigration {
    id: string;
    columnIds: string[];
    action?: 'hide' | 'show';
}

export interface IHiddenColumnsMigrationResult {
    // Undefined when there is no saved list: the column defaults already apply.
    hiddenColumns: string[] | undefined;
    appliedMigrations: string[];
}

export function migrateHiddenColumns(
    savedHiddenColumns: string[] | undefined,
    appliedMigrations: string[] | undefined,
    migration: IHiddenColumnsMigration,
): IHiddenColumnsMigrationResult | undefined {
    const applied = appliedMigrations || [];
    if (applied.includes(migration.id)) {
        return undefined;
    }

    let hiddenColumns: string[] | undefined;
    if (savedHiddenColumns) {
        hiddenColumns =
            migration.action === 'show'
                ? savedHiddenColumns.filter(id => !migration.columnIds.includes(id))
                : [...savedHiddenColumns, ...migration.columnIds.filter(id => !savedHiddenColumns.includes(id))];
    }

    return {
        hiddenColumns,
        appliedMigrations: [...applied, migration.id],
    };
}

/**
 * Apply a migration to one table's saved settings. Call it BEFORE the table's `DataTablePresenter` is built:
 * the presenter reads the hidden columns once, in its constructor.
 */
export function applyHiddenColumnsMigration(tableName: string, migration: IHiddenColumnsMigration): void {
    const result = migrateHiddenColumns(
        TableStorage.getItem<string[]>(tableName, 'hidden_columns'),
        TableStorage.getItem<string[]>(tableName, 'hidden_columns_migrations'),
        migration,
    );
    if (!result) {
        return;
    }
    if (result.hiddenColumns) {
        TableStorage.saveItem(tableName, 'hidden_columns', result.hiddenColumns);
    }
    TableStorage.saveItem(tableName, 'hidden_columns_migrations', result.appliedMigrations);
}
